import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../../context/StoreContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Image as ImageIcon, Upload, Trash, PencilSimple, MagnifyingGlass,
  ArrowsClockwise, DownloadSimple, CloudArrowUp, X, Check, FileImage,
  GridFour, Rows, SquaresFour, Link as LinkIcon, Warning
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MediaPage = () => {
  const { currentStore } = useStore();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [seoName, setSeoName] = useState('');
  const [quality, setQuality] = useState('medium');
  const [syncing, setSyncing] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('mv_media_view') || 'grid');
  const [seoFilter, setSeoFilter] = useState('all'); // all | seo_ok | seo_bad
  const [usagePanel, setUsagePanel] = useState(null); // { media, items[] }
  useEffect(() => { localStorage.setItem('mv_media_view', viewMode); }, [viewMode]);

  // Format file size: KB if < 1024, MB otherwise
  const fmtSize = (kb) => {
    if (!kb || kb <= 0) return null;
    if (kb >= 1024) return `${(kb / 1024).toFixed(1)} Mo`;
    return `${Math.round(kb)} Ko`;
  };

  // SEO friendliness: lowercase, hyphens (no underscore), no random hash, ≤60 chars
  const isSeoFriendly = (m) => {
    const name = (m.seo_name || m.filename || '').toLowerCase();
    if (!name) return false;
    const base = name.replace(/\.[^.]+$/, '');
    if (base.length < 3 || base.length > 70) return false;
    if (/_/.test(base)) return false;                     // underscores not SEO
    if (/[A-Z]/.test(m.seo_name || m.filename || '')) return false;
    if (/[0-9a-f]{8,}/.test(base)) return false;          // hash-like
    if (/^(img|image|dsc|adobestock|i\d+|untitled|photo)/.test(base)) return false;
    if (!/[a-z]{3,}/.test(base)) return false;
    return true;
  };

  const storeId = currentStore?.id;

  const fetchMedia = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/api/stores/${storeId}/media`, { withCredentials: true });
      setMedia(resp.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const migrateToCloud = async () => {
    if (!window.confirm("Basculer toutes les images existantes (catégories, produits, logos, médias) vers le stockage cloud permanent ?\n\nLes fichiers survivront définitivement aux redéploiements. Durée : quelques minutes.")) return;
    setMigrating(true);
    let total = 0, refs = 0, guard = 0;
    try {
      while (guard < 60) {
        const { data } = await axios.post(`${API}/api/admin/migrate-images-to-cloud?batch=25`, {}, { withCredentials: true, timeout: 180000 });
        total += data.migrated; refs += data.updated_refs; guard++;
        if (data.remaining <= 0) break;
        if (data.migrated === 0) break;
        toast.info(`Migration… ${total} fichier(s) migré(s), ${data.remaining} restant(s)`, { duration: 3000 });
      }
      toast.success(`Migration terminée : ${total} fichier(s) sur le stockage permanent, ${refs} référence(s) mise(s) à jour ✅`, { duration: 8000 });
      fetchMedia();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur pendant la migration');
    } finally { setMigrating(false); }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length || !storeId) return;
    setUploading(true);
    let count = 0;
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      form.append('quality', quality);
      try {
        await axios.post(`${API}/api/stores/${storeId}/media/upload`, form, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        count++;
      } catch (err) {
        toast.error(`Erreur upload : ${file.name}`);
      }
    }
    toast.success(`${count} fichier(s) uploadé(s)`);
    setUploading(false);
    fetchMedia();
    e.target.value = '';
  };

  const handleDownloadUrl = async () => {
    if (!downloadUrl || !storeId) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('url', downloadUrl);
      form.append('seo_name', seoName);
      form.append('quality', quality);
      await axios.post(`${API}/api/stores/${storeId}/media/download-url`, form, { withCredentials: true });
      toast.success('Image téléchargée et compressée');
      setUrlDialogOpen(false);
      setDownloadUrl('');
      setSeoName('');
      fetchMedia();
    } catch (err) {
      toast.error('Erreur de téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!storeId) return;
    try {
      await axios.delete(`${API}/api/stores/${storeId}/media/${id}`, { withCredentials: true });
      toast.success('Fichier supprimé');
      setMedia(prev => prev.filter(m => m.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (err) {
      toast.error('Erreur de suppression');
    }
  };

  const handleRename = async () => {
    if (!selected || !storeId || !renameName) return;
    try {
      const form = new FormData();
      form.append('new_name', renameName);
      await axios.put(`${API}/api/stores/${storeId}/media/${selected.id}/rename`, form, { withCredentials: true });
      toast.success('Fichier renommé');
      setRenameOpen(false);
      fetchMedia();
    } catch (err) {
      toast.error('Erreur de renommage');
    }
  };

  const handleCompress = async (id, q) => {
    if (!storeId) return;
    try {
      const form = new FormData();
      form.append('media_id', id);
      form.append('quality', q);
      await axios.post(`${API}/api/stores/${storeId}/media/compress`, form, { withCredentials: true });
      toast.success('Image recompressée');
      fetchMedia();
    } catch (err) {
      toast.error('Erreur de compression');
    }
  };

  const handleSyncProducts = async () => {
    if (!storeId) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/api/stores/${storeId}/media/sync-products`, {}, { withCredentials: true });
      toast.success(res.data.message || `${res.data.imported} images importées`);
      fetchMedia();
    } catch (err) {
      toast.error('Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const getImageSrc = (m) => {
    const url = m.url || '';
    if (url.startsWith('http')) return url;
    return `${API}${url}`;
  };

  const filtered = media.filter(m => {
    if (search && !(m.filename?.toLowerCase().includes(search.toLowerCase()) || m.original_filename?.toLowerCase().includes(search.toLowerCase()) || m.seo_name?.toLowerCase().includes(search.toLowerCase()))) return false;
    if (seoFilter === 'seo_ok' && !isSeoFriendly(m)) return false;
    if (seoFilter === 'seo_bad' && isSeoFriendly(m)) return false;
    return true;
  });

  const seoStats = {
    ok: media.filter(isSeoFriendly).length,
    bad: media.filter(m => !isSeoFriendly(m)).length,
  };

  const openUsagePanel = async (m) => {
    setUsagePanel({ media: m, loading: true, data: null });
    try {
      const { data } = await axios.get(`${API}/api/stores/${storeId}/media/${m.id}/usage`, { withCredentials: true });
      setUsagePanel({ media: m, loading: false, data });
    } catch (e) {
      toast.error('Erreur de chargement');
      setUsagePanel(null);
    }
  };

  if (!currentStore) return (
    <div className="flex items-center justify-center h-64 text-slate-400" data-testid="media-no-store">
      <p>Sélectionnez une boutique pour gérer vos médias</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="media-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Médias</h1>
          <p className="text-sm text-slate-500">
            {media.length} fichier(s) ·{' '}
            <span className="text-emerald-600 font-medium">{seoStats.ok} SEO OK</span> ·{' '}
            <span className="text-amber-600 font-medium">{seoStats.bad} à optimiser</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={migrateToCloud} disabled={migrating} data-testid="migrate-cloud-btn" title="Bascule toutes les images vers un stockage qui survit aux redéploiements">
            <ArrowsClockwise size={16} className={`mr-2 ${migrating ? 'animate-spin' : ''}`} /> {migrating ? 'Migration…' : '☁️ Migrer vers le cloud'}
          </Button>
          <Button variant="outline" onClick={handleSyncProducts} disabled={syncing} data-testid="media-sync-btn">
            <ArrowsClockwise size={16} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Sync...' : 'Sync produits'}
          </Button>
          <Button variant="outline" onClick={async () => {
            try {
              const r = await axios.post(`${API}/api/stores/${storeId}/media/backfill-metadata`, {}, { withCredentials: true });
              toast.success(`Métadonnées mises à jour : ${r.data.updated} / ${r.data.updated + r.data.missing}`);
              fetchMedia();
            } catch { toast.error('Erreur backfill'); }
          }} data-testid="media-backfill-btn" title="Recalculer poids et dimensions">
            <ArrowsClockwise size={16} className="mr-2" /> Maj poids
          </Button>
          <Button variant="outline" onClick={() => setUrlDialogOpen(true)} data-testid="media-url-btn">
            <CloudArrowUp size={16} className="mr-2" /> Depuis URL
          </Button>
          <label className="cursor-pointer">
            <Button asChild disabled={uploading}>
              <span><Upload size={16} className="mr-2" /> {uploading ? 'Upload...' : 'Uploader'}</span>
            </Button>
            <input type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} data-testid="media-upload-input" />
          </label>
        </div>
      </div>

      {/* Search & Quality + View modes + SEO filter */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="pl-9 h-9" data-testid="media-search" />
        </div>
        {/* SEO filter */}
        <Select value={seoFilter} onValueChange={setSeoFilter}>
          <SelectTrigger className="w-[180px] h-9" data-testid="media-seo-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les images</SelectItem>
            <SelectItem value="seo_ok">✅ SEO OK</SelectItem>
            <SelectItem value="seo_bad">⚠️ À optimiser</SelectItem>
          </SelectContent>
        </Select>
        {/* View toggle */}
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
          <button onClick={() => setViewMode('grid')} title="Vue cartes (grandes)"
                  className={`px-2.5 py-1.5 ${viewMode === 'grid' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="media-view-grid">
            <SquaresFour size={16} />
          </button>
          <button onClick={() => setViewMode('compact')} title="Vue compacte (8 col.)"
                  className={`px-2.5 py-1.5 ${viewMode === 'compact' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="media-view-compact">
            <GridFour size={16} />
          </button>
          <button onClick={() => setViewMode('table')} title="Vue tableau"
                  className={`px-2.5 py-1.5 ${viewMode === 'table' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="media-view-table">
            <Rows size={16} />
          </button>
        </div>
        <Select value={quality} onValueChange={setQuality}>
          <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Qualité" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="high">Haute (90%)</SelectItem>
            <SelectItem value="medium">Moyenne (75%)</SelectItem>
            <SelectItem value="low">Basse (60%)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <FileImage size={48} className="mx-auto mb-3" />
          <p className="text-sm mb-4">Aucun média dans la bibliothèque.</p>
          <Button variant="outline" onClick={handleSyncProducts} disabled={syncing} data-testid="media-sync-empty-btn">
            <ArrowsClockwise size={16} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Import en cours...' : 'Importer les images des produits'}
          </Button>
        </div>
      ) : viewMode === 'table' ? (
        // ── TABLE VIEW ──
        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full text-sm" data-testid="media-table">
            <thead className="bg-slate-50">
              <tr className="text-left text-[11px] uppercase text-slate-500">
                <th className="px-3 py-2 w-[60px]">Aperçu</th>
                <th className="px-3 py-2">Nom</th>
                <th className="px-3 py-2 text-center">SEO</th>
                <th className="px-3 py-2 text-center">Utilisée</th>
                <th className="px-3 py-2">Dim.</th>
                <th className="px-3 py-2">Taille</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(m)} data-testid={`media-row-${m.id}`}>
                  <td className="px-3 py-2"><img src={getImageSrc(m)} alt="" className="w-10 h-10 rounded object-cover border" loading="lazy" /></td>
                  <td className="px-3 py-2 max-w-[300px]">
                    <p className="font-medium truncate text-slate-800">{m.seo_name || m.filename}</p>
                    <p className="text-[11px] text-slate-400 truncate">{m.filename}</p>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {isSeoFriendly(m)
                      ? <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-700"><Check size={11} weight="bold" /> OK</span>
                      : <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-700"><Warning size={11} weight="fill" /> À optimiser</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {m.usage_count > 0 ? (
                      <button className="inline-flex items-center gap-1 text-[12px] text-blue-600 hover:underline" onClick={(e) => { e.stopPropagation(); openUsagePanel(m); }} data-testid={`media-usage-${m.id}`}>
                        <LinkIcon size={12} /> {m.usage_count}
                      </button>
                    ) : <span className="text-[12px] text-slate-300">0</span>}
                  </td>
                  <td className="px-3 py-2 text-[12px] text-slate-500">{m.width ? `${m.width}×${m.height}` : '—'}</td>
                  <td className="px-3 py-2 text-[12px] text-slate-500">{fmtSize(m.compressed_size_kb) || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={e => { e.stopPropagation(); setSelected(m); setRenameName(m.filename.replace(/\.[^.]+$/, '')); setRenameOpen(true); }} className="p-1 hover:bg-slate-100 rounded" title="Renommer"><PencilSimple size={14} /></button>
                    <button onClick={e => { e.stopPropagation(); handleDelete(m.id); }} className="p-1 hover:bg-red-50 rounded text-red-500" title="Supprimer"><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={viewMode === 'compact'
          ? "grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-2"
          : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3"}
          data-testid={`media-${viewMode}`}>
          {filtered.map(m => (
            <div key={m.id}
              className={`group relative bg-white border rounded-lg overflow-hidden cursor-pointer transition-all hover:shadow-md ${selected?.id === m.id ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setSelected(m)}
              data-testid={`media-item-${m.id}`}>
              <div className="aspect-square bg-slate-50 flex items-center justify-center relative">
                <img src={getImageSrc(m)} alt={m.filename} className="w-full h-full object-cover" loading="lazy"
                  onError={e => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></div>'; }} />
                {/* SEO + usage overlay */}
                <div className="absolute top-1 left-1 flex flex-col gap-1">
                  {isSeoFriendly(m)
                    ? <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] bg-emerald-600 text-white" title="Nom SEO OK"><Check size={9} weight="bold" /></span>
                    : <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] bg-amber-500 text-white" title="Nom à optimiser pour le SEO"><Warning size={9} weight="fill" /></span>}
                  {m.usage_count > 0 && (
                    <button onClick={(e) => { e.stopPropagation(); openUsagePanel(m); }}
                            className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] bg-blue-600 text-white hover:bg-blue-700" title="Pages utilisant cette image">
                      <LinkIcon size={9} /> {m.usage_count}
                    </button>
                  )}
                </div>
              </div>
              {viewMode === 'grid' && (
                <div className="p-2">
                  <p className="text-[11px] font-medium text-slate-700 truncate">{m.seo_name || m.filename}</p>
                  <p className="text-[10px] text-slate-400">
                    {fmtSize(m.compressed_size_kb) || (m.source === 'woocommerce' ? 'WooCommerce' : '—')}
                    {m.width ? ` · ${m.width}×${m.height}` : ''}
                  </p>
                </div>
              )}
              {/* Actions overlay */}
              <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={e => { e.stopPropagation(); setSelected(m); setRenameName(m.filename.replace(/\.[^.]+$/, '')); setRenameOpen(true); }}
                  className="w-6 h-6 rounded bg-white/90 flex items-center justify-center shadow hover:bg-white" title="Renommer SEO">
                  <PencilSimple size={12} />
                </button>
                <button onClick={e => { e.stopPropagation(); handleCompress(m.id, 'low'); }}
                  className="w-6 h-6 rounded bg-white/90 flex items-center justify-center shadow hover:bg-white" title="Recompresser">
                  <ArrowsClockwise size={12} />
                </button>
                <button onClick={e => { e.stopPropagation(); handleDelete(m.id); }}
                  className="w-6 h-6 rounded bg-red-50 flex items-center justify-center shadow hover:bg-red-100 text-red-500" title="Supprimer">
                  <Trash size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Usage panel */}
      {usagePanel && (
        <Dialog open={!!usagePanel} onOpenChange={(o) => !o && setUsagePanel(null)}>
          <DialogContent className="max-w-lg" data-testid="media-usage-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><LinkIcon size={18} /> Utilisée dans {usagePanel.data?.total ?? '…'} page(s)</DialogTitle>
            </DialogHeader>
            <div className="flex gap-3 items-start">
              <img src={getImageSrc(usagePanel.media)} alt="" className="w-16 h-16 rounded object-cover border" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{usagePanel.media.seo_name || usagePanel.media.filename}</p>
                <p className="text-xs text-slate-400 truncate">{usagePanel.media.url}</p>
              </div>
            </div>
            {usagePanel.loading ? (
              <p className="text-sm text-slate-500 text-center py-4">Chargement…</p>
            ) : !usagePanel.data?.total ? (
              <p className="text-sm text-slate-500 text-center py-6">Cette image n'est utilisée sur aucune page.</p>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {usagePanel.data.products?.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase font-bold text-slate-500 mb-1.5">📦 Produits ({usagePanel.data.products.length})</p>
                    <ul className="space-y-1">
                      {usagePanel.data.products.map(p => (
                        <li key={p.id}>
                          <a href={`/admin/products/${p.id}/edit`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline block truncate">→ {p.name}</a>
                          {p.slug && <a href={`/product/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-slate-400 hover:underline block">  /product/{p.slug}</a>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {usagePanel.data.blog_posts?.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase font-bold text-slate-500 mb-1.5">📝 Articles de blog ({usagePanel.data.blog_posts.length})</p>
                    <ul className="space-y-1">
                      {usagePanel.data.blog_posts.map(p => (
                        <li key={p.id}><a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">→ {p.name}</a></li>
                      ))}
                    </ul>
                  </div>
                )}
                {usagePanel.data.cms_pages?.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase font-bold text-slate-500 mb-1.5">📄 Pages CMS ({usagePanel.data.cms_pages.length})</p>
                    <ul className="space-y-1">
                      {usagePanel.data.cms_pages.map(p => (
                        <li key={p.id}><a href={`/page/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">→ {p.name}</a></li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Detail panel for selected */}
      {selected && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50" data-testid="media-detail-panel">
          <div className="max-w-5xl mx-auto flex items-center gap-4">
            <img src={getImageSrc(selected)} alt="" className="w-16 h-16 object-cover rounded border" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{selected.filename}</p>
              <p className="text-xs text-slate-400">
                {selected.width}×{selected.height} · {fmtSize(selected.compressed_size_kb) || '—'}
                {selected.original_size_kb && ` (original: ${fmtSize(selected.original_size_kb)}, -${selected.compression_ratio}%)`}
              </p>
              <p className="text-xs text-slate-400 truncate">URL: {getImageSrc(selected)}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setRenameName(selected.filename.replace(/\.[^.]+$/, '')); setRenameOpen(true); }}>
                <PencilSimple size={14} className="mr-1" /> Renommer
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleCompress(selected.id, 'low')}>
                <ArrowsClockwise size={14} className="mr-1" /> Compresser
              </Button>
              <Button size="sm" variant="destructive" onClick={() => handleDelete(selected.id)}>
                <Trash size={14} className="mr-1" /> Supprimer
              </Button>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-slate-100 rounded">
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Renommage SEO</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Entrez le nouveau nom SEO-friendly (sans extension) :</p>
            <Input value={renameName} onChange={e => setRenameName(e.target.value)} placeholder="nom-seo-du-fichier" data-testid="rename-input" />
            <p className="text-xs text-slate-400">Fichier actuel : {selected?.filename}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>Annuler</Button>
            <Button onClick={handleRename} data-testid="rename-confirm-btn"><Check size={14} className="mr-1" /> Renommer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download from URL Dialog */}
      <Dialog open={urlDialogOpen} onOpenChange={setUrlDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Télécharger depuis une URL</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={downloadUrl} onChange={e => setDownloadUrl(e.target.value)} placeholder="https://example.com/image.jpg" data-testid="url-input" />
            <Input value={seoName} onChange={e => setSeoName(e.target.value)} placeholder="Nom SEO (optionnel)" />
            <Select value={quality} onValueChange={setQuality}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">Haute qualité</SelectItem>
                <SelectItem value="medium">Moyenne</SelectItem>
                <SelectItem value="low">Basse</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUrlDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleDownloadUrl} disabled={uploading} data-testid="url-download-btn">
              <DownloadSimple size={14} className="mr-1" /> {uploading ? 'Téléchargement...' : 'Télécharger'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaPage;
